import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { store, useAppSelector } from '@/app/store';

import Button from '@/components/Common/Button';
import CheckBox from '@/components/Common/CheckBox';
import DigitInput from '@/components/Common/DigitInput';
import FlexRow from '@/components/Common/FlexRow';
import Input from '@/components/Common/Input';

import { ForgotPasswordModal, PasswordResetLanding } from './ForgotPassword';

const whitespaceRegex = /\s/g;

const Login = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const [user, setUser] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPw, setShowPw] = React.useState(false);
  const [rememberMe, setRememberMe] = React.useState(false);
  const [triedSave, SetTriedSave] = React.useState(false);

  // Step 2 state: 6-digit TOTP or xxxx-xxxx recovery code.
  const [code, setCode] = React.useState("");
  const [verifying, setVerifying] = React.useState(false);
  // Whether the user is entering a TOTP code (default) or a recovery code.
  // Recovery codes have a dash so they don't fit the 6-digit segmented input
  // — toggling here swaps to a single text field.
  const [verifyMode, setVerifyMode] = React.useState<'totp' | 'recovery'>('totp');

  const [forgotPass, setForgotPass] = React.useState(false);

  const [projectLogin, setProjectLogin] = React.useState(false);
  const [projectId, setProjectId] = React.useState("");

  // Reading 2FA challenge state from Redux. After /login.json returns
  // requires_2fa, the slice gets populated and we re-render in step-2 mode.
  const requires2FA = useAppSelector((state) => state.logins.requires2FA);
  const ticketExpiresAt = useAppSelector((state) => state.logins.ticketExpiresAt);
  const lockedUntil = useAppSelector((state) => state.logins.lockedUntil);
  const methods = useAppSelector((state) => state.logins.methods);
  const hasTotp = methods.includes('totp');
  const hasEmail = methods.includes('email');
  const hasRecovery = methods.includes('recovery');
  const [selectedMethod, setSelectedMethod] = React.useState<'totp' | 'email'>('totp');
  React.useEffect(() => {
    if (!requires2FA) return;
    setSelectedMethod(hasTotp ? 'totp' : (hasEmail ? 'email' : 'totp'));
    setVerifyMode('totp');
    setCode("");
    setEmailCodeSent(false);
    setEmailCooldown(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requires2FA]);

  // Email-method sub-state: whether a code has been requested, and a
  // resend cooldown (seconds) so the user can't spam "send".
  const [emailCodeSent, setEmailCodeSent] = React.useState(false);
  const [emailSending, setEmailSending] = React.useState(false);
  const [emailCooldown, setEmailCooldown] = React.useState(0);
  React.useEffect(() => {
    if (emailCooldown <= 0) return;
    const id = setInterval(() => setEmailCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [emailCooldown]);

  // Lockout countdown: re-render every second while a lockout is active so
  // the remaining-time label updates and the input flips back to enabled
  // the moment the timestamp passes. The effect self-clears the lockout
  // in Redux once the deadline is reached so other listeners stay in sync.
  const [now, setNow] = React.useState<number>(() => Date.now());
  React.useEffect(() => {
    if (!lockedUntil) return;
    const deadline = new Date(lockedUntil).getTime();
    if (isNaN(deadline)) return;
    if (deadline <= Date.now()) {
      store.clearLockout();
      return;
    }
    const tick = setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (t >= deadline) {
        store.clearLockout();
        clearInterval(tick);
      }
    }, 1000);
    return () => clearInterval(tick);
  }, [lockedUntil]);

  const lockoutSecondsLeft = React.useMemo(() => {
    if (!lockedUntil) return 0;
    const deadline = new Date(lockedUntil).getTime();
    if (isNaN(deadline)) return 0;
    return Math.max(0, Math.ceil((deadline - now) / 1000));
  }, [lockedUntil, now]);
  const isLockedOut = lockoutSecondsLeft > 0;

  const userInvalid = React.useMemo(() => {
    const userTrim = user.trim() != user;
    const whitespace = whitespaceRegex.test(user);
    return userTrim || whitespace;
  }, [user]);

  const passwordInvalid = React.useMemo(() => {
    return password.trim() != password;
  }, [password]);

  const projectIdInvalid = React.useMemo(() => {
    const idTrim = projectId.trim() != projectId;
    const whitespace = whitespaceRegex.test(projectId);
    return idTrim || whitespace;
  }, [projectId]);

  // Code is valid as either a 6-digit TOTP or a recovery code containing
  // at least one alphanumeric character with a dash separator. We keep
  // the client-side check loose; the server is the source of truth.
  const codeInvalid = React.useMemo(() => {
    const trimmed = code.trim();
    if (trimmed.length === 0) return true;
    const totp = /^\d{6}$/.test(trimmed);
    const recovery = /^[a-z0-9]{3,}-[a-z0-9]{3,}$/i.test(trimmed);
    return !(totp || recovery);
  }, [code]);

  const handleLogin = React.useCallback(async () => {
    if (userInvalid || passwordInvalid) {
      SetTriedSave(true);
      return;
    }
    SetTriedSave(false);
    await store.performLogin(user, password, rememberMe);
    // If 2FA is required the store sets state.logins.requires2FA and the
    // component re-renders into step-2 mode automatically. RememberMe was
    // already captured on the ticket server-side at step 1.
  }, [user, password, rememberMe, userInvalid, passwordInvalid]);

  const handleProjectLogin = React.useCallback(async () => {
    const empty = user.trim() === "" || projectId.trim() === "" || password.trim() === "";
    if (empty || userInvalid || passwordInvalid || projectIdInvalid) {
      SetTriedSave(true);
      return;
    }
    SetTriedSave(false);
    // Local users log in with the qualified form "username%project_id";
    // from here the normal flow (incl. 2FA) takes over via requires2FA.
    await store.performLogin(`${user.trim()}%${projectId.trim()}`, password, rememberMe);
  }, [user, projectId, password, rememberMe, userInvalid, passwordInvalid, projectIdInvalid]);

  const backToLogin = React.useCallback(() => {
    setProjectLogin(false);
    setProjectId("");
    SetTriedSave(false);
  }, []);

  const handleVerify = React.useCallback(async () => {
    if (isLockedOut) return;
    if (codeInvalid) {
      SetTriedSave(true);
      return;
    }
    SetTriedSave(false);
    setVerifying(true);
    try {
      // Map the UI selection to the backend method: email, recovery, or
      // totp. Recovery is a sub-mode of the authenticator selection.
      const method = selectedMethod === 'email'
        ? 'email'
        : (verifyMode === 'recovery' ? 'recovery' : 'totp');
      const outcome = await store.performLogin2FA(code.trim(), method);
      if (outcome !== "success") {
        // Clear the input so the user can try again; the error message
        // dispatched by the store renders elsewhere via the existing
        // status banner. "locked" outcome populates lockedUntil so the
        // input transitions into the countdown state on next render.
        setCode("");
      }
    } finally {
      setVerifying(false);
    }
  }, [code, codeInvalid, isLockedOut, selectedMethod, verifyMode]);

  // sendEmailCode requests an email login code for the in-flight ticket
  // and arms a 30s resend cooldown. Mirrors the resend UX of the email
  // verification modal.
  const handleSendEmailCode = React.useCallback(async () => {
    if (emailSending || emailCooldown > 0) return;
    setEmailSending(true);
    try {
      const outcome = await store.sendLogin2FAEmailCode();
      if (outcome === "ok") {
        setEmailCodeSent(true);
        setCode("");
        setEmailCooldown(30);
      } else if (outcome === "locked") {
        // Throttled — still arm the cooldown so the button disables.
        setEmailCooldown(30);
      } else {
        // "failure" / "no_ticket": apiOperation may have toasted a 503,
        // but surface a clear inline message so the user isn't left on a
        // dead-end Send screen with no feedback.
        store.dispatchError("Could not send the code. Please try again.");
      }
    } catch (_e) {
      store.dispatchError("Network error sending the code. Please try again.");
    } finally {
      setEmailSending(false);
    }
  }, [emailSending, emailCooldown]);

  const handleCancel2FA = React.useCallback(() => {
    store.cancel2FAChallenge();
    setCode("");
    setVerifyMode('totp');
    setSelectedMethod('totp');
    setEmailCodeSent(false);
    setEmailCooldown(0);
    SetTriedSave(false);
  }, []);

  const cannotLogin = (triedSave && (userInvalid || passwordInvalid));
  const cannotProjectLogin = (triedSave && (userInvalid || passwordInvalid || projectIdInvalid ||
                              user.trim() === "" || projectId.trim() === "" || password.trim() === ""));
  const cannotVerify = (triedSave && codeInvalid);

  // 2FA step-2 uses a dedicated two-column "Verify it's you" card.
  // Plain <div>s here because FlexRow's defaults (items-center, flex
  // basis 0%) collapse the side-by-side panels. The password screen below
  // keeps its original FlexRow-based single-column layout.
  if (requires2FA) {
    return (
    <div className="fixed inset-0 bg-[--Light-Grey] overflow-y-auto py-[24px] flex flex-col">
      <div className="
             w-full
             max-w-[760px]
             mx-auto
             my-auto
             loginBanner
             rounded-[4px]
             overflow-hidden
             flex
             flex-row
             items-stretch
             bg-white">
        {/* Left panel — branding + reassurance */}
        <div className="bg-[--Primetime-main] text-white p-[28px] flex flex-col gap-[14px] w-[300px] flex-shrink-0">
          <img src="/assets/img/logo_lg.png"
               className="w-[140px]" />
          <div className="bg-white/15 rounded-[8px] w-[44px] h-[44px] flex items-center justify-center mt-[24px]">
            <span className="material-icons !text-white text-[24px]">lock</span>
          </div>
          <h2 className="text-[20px] font-bold m-0 mt-[8px]">
            Verify it's you
          </h2>
          <p className="text-[13px] opacity-90 m-0 leading-[18px]">
            An additional security step keeps your Primetime account safe — even if your password is compromised.
          </p>
        </div>
        {/* Right panel — code entry */}
        <div className="bg-white p-[28px] flex flex-col gap-[14px] flex-1 min-w-0">
          <span onClick={handleCancel2FA}
                className="flex items-center gap-[4px] text-[--Dark-Grey] text-[13px] cursor-pointer hover:underline w-fit">
            <span className="material-icons text-[16px]">arrow_back</span>
            Back to login
          </span>
          <p className="text-[12px] text-[--Dark-Grey] m-0">
            Signed in as <strong>{user}</strong>
          </p>
          {/* Method chooser — shown only when more than one top-level
              factor is available (authenticator AND email). */}
          { hasTotp && hasEmail &&
            <div className="flex flex-row gap-[8px]">
              <button type="button"
                      onClick={() => { setSelectedMethod('totp'); setVerifyMode('totp'); setCode(""); }}
                      className={`flex-1 text-[13px] py-[8px] rounded-[4px] border-solid border-1 ${selectedMethod === 'totp' ? 'border-[--Primetime-main] text-[--Primetime-main] font-bold' : 'border-[--Mid-Grey] text-[--Dark-Grey]'}`}>
                Authenticator app
              </button>
              <button type="button"
                      onClick={() => { setSelectedMethod('email'); setVerifyMode('totp'); setCode(""); }}
                      className={`flex-1 text-[13px] py-[8px] rounded-[4px] border-solid border-1 ${selectedMethod === 'email' ? 'border-[--Primetime-main] text-[--Primetime-main] font-bold' : 'border-[--Mid-Grey] text-[--Dark-Grey]'}`}>
                Email code
              </button>
            </div>
          }
          <h1 className="text-[20px] font-bold m-0">
            { selectedMethod === 'email' ? "Enter your email code" : "Enter your authentication code" }
          </h1>
          <p className="text-[13px] text-[--Dark-Grey] m-0 leading-[18px]">
            { selectedMethod === 'email'
              ? (emailCodeSent
                  ? "We sent a 6-digit code to your registered email. Enter it below."
                  : "We'll email a 6-digit code to your registered address.")
              : (verifyMode === 'totp'
                  ? "Open the authenticator app on your phone and enter the 6-digit code shown."
                  : "Enter one of the recovery codes you saved when you first set up 2FA.") }
          </p>
          { ticketExpiresAt &&
            <p className="text-[11px] text-[--Dark-Grey] m-0">
              Ticket expires at { new Date(ticketExpiresAt).toLocaleTimeString() }.
            </p>
          }
          { isLockedOut &&
            <div className="bg-[--Light-Grey] border border-[--Error] rounded-[4px] p-[12px] flex flex-col gap-[4px]">
              <span className="text-[--Error] text-[13px] font-bold">
                Too many failed attempts.
              </span>
              <span className="text-[--Dark-Grey] text-[13px]">
                Try again at { new Date(lockedUntil).toLocaleTimeString() } ({ lockoutSecondsLeft }s).
              </span>
            </div>
          }
          <div className="py-[8px]">
            { selectedMethod === 'email'
              ? ( emailCodeSent
                    ? <DigitInput value={code}
                                  onChange={setCode}
                                  disabled={isLockedOut || verifying}
                                  autoFocus />
                    : <Button caption={emailSending ? "Sending…" : "Send code to my email"}
                              layout={emailSending ? 'warning_bg' : 'default'}
                              isDisabled={emailSending}
                              onClick={handleSendEmailCode} /> )
              : ( verifyMode === 'totp'
                    ? <DigitInput value={code}
                                  onChange={setCode}
                                  disabled={isLockedOut || verifying}
                                  autoFocus />
                    : <Input value={code}
                             label="Recovery code"
                             onValueChange={setCode}
                             type="text"
                             isDisabled={isLockedOut || verifying}
                             placeholder="xxxx-xxxx"
                             classNames={{
                               inputWrapper: "px-[12px] py-[8px] rounded-[4px] border-solid border-1 border-[--Mid-Grey]",
                               input: "tracking-[0.15em] font-mono",
                             }} /> )
            }
          </div>
          <div className="flex flex-row justify-between items-center mt-[4px]">
            { selectedMethod === 'email'
              ? ( emailCodeSent
                    ? <span onClick={handleSendEmailCode}
                            className={`text-[13px] ${(emailCooldown > 0 || emailSending) ? 'text-[--Mid-Grey] cursor-default' : 'text-[--Dark-Grey] cursor-pointer hover:underline'}`}>
                        { emailCooldown > 0 ? `Resend in ${emailCooldown}s` : 'Resend code' }
                      </span>
                    : <span /> )
              : ( hasRecovery
                    ? <span onClick={() => { setVerifyMode(verifyMode === 'totp' ? 'recovery' : 'totp'); setCode(""); }}
                            className="text-[--Dark-Grey] text-[13px] cursor-pointer hover:underline">
                        { verifyMode === 'totp' ? 'Try another way' : 'Use authenticator code' }
                      </span>
                    : <span /> )
            }
            { !(selectedMethod === 'email' && !emailCodeSent) &&
              <Button caption={isLockedOut
                                ? `Locked (${lockoutSecondsLeft}s)`
                                : verifying ? "Verifying…" : "Verify and continue"}
                      layout={isLockedOut || cannotVerify || verifying ? 'warning_bg' : 'default'}
                      isDisabled={isLockedOut || cannotVerify || verifying}
                      onClick={handleVerify} />
            }
          </div>
          <div className="flex justify-center text-[--Dark-Grey] text-[13px] p-[4px] cursor-pointer hover:underline">
            <span onClick={handleCancel2FA}>
              Use a different account
            </span>
          </div>
        </div>
      </div>
    </div>
    );
  }

  if (location.pathname === '/reset') {
    return <PasswordResetLanding />;
  }


  if (projectLogin) {
    return (
    <FlexRow direction="column"
             justify="center"
             className="absolute right-0 left-0 top-0 bottom-0 bg-[--Light-Grey]">
      <FlexRow direction="column"
               flex={0}
               className="max-w-[560px] min-h-[360px] loginBanner">
        <FlexRow className="bg-[--Primetime-main]"
                 justify="center"
                 flex={0}>
          <img src="/assets/img/logo_lg.png"
               className="w-[164px]" />
        </FlexRow>
        <FlexRow direction="column"
                 justify="center"
                 className="px-[32px] py-[16px] gap-[16px] bg-white">
          <h1 className="r14 text-black font-bold">
            { t('login:login_to_project') }
          </h1>
          <Input value={user}
                 label={t('login:username:caption') + '*'}
                 onValueChange={setUser}
                 type="text"
                 labelPlacement="outside"
                 autoComplete="off"
                 placeholder={t('login:username:caption')}
                 isInvalid={triedSave && (userInvalid || user.trim() === "")}
                 errorMessage={t('login:username:invalid')}
                 classNames={{
                   inputWrapper: "px-[16px] py-[8px] rounded-[4px] border-solid border-1 border-[--Mid-Grey] !bg-white",
                   input: "!text-black",
                   label: "!text-[--Dark-Grey] pb-[4px]",
                 }} />
          <Input value={projectId}
                 label={t('login:project_id:caption') + '*'}
                 onValueChange={setProjectId}
                 type="text"
                 labelPlacement="outside"
                 autoComplete="off"
                 placeholder={t('login:project_id:caption')}
                 isInvalid={triedSave && (projectIdInvalid || projectId.trim() === "")}
                 errorMessage={t('login:project_id:invalid')}
                 classNames={{
                   inputWrapper: "px-[16px] py-[8px] rounded-[4px] border-solid border-1 border-[--Mid-Grey] !bg-white",
                   input: "!text-black",
                   label: "!text-[--Dark-Grey] pb-[4px]",
                 }} />
          <Input value={password}
                 label={t('login:password:caption') + '*'}
                 onValueChange={setPassword}
                 type={showPw ? 'text' : 'password'}
                 labelPlacement="outside"
                 autoComplete="new-password"
                 placeholder={t('login:password:caption')}
                 isInvalid={triedSave && (passwordInvalid || password.trim() === "")}
                 errorMessage={t('login:password:invalid')}
                 classNames={{
                   inputWrapper: "px-[16px] py-[8px] rounded-[4px] border-solid border-1 border-[--Mid-Grey] !bg-white",
                   input: "!text-black",
                   label: "!text-[--Dark-Grey] pb-[4px]",
                 }}
                 endContent={
                 <span className="material-icons cursor-pointer"
                       onClick={() => setShowPw(!showPw)}>
                 { showPw ? 'visibility_off' : 'visibility' }
                 </span>
                 } />
          <FlexRow flex={0}
                   className="gap-[8px] items-center cursor-pointer select-none"
                   onClick={() => setRememberMe(!rememberMe)}>
            <CheckBox toggled={rememberMe}
                      setToggled={setRememberMe}
                      size="text-[24px]" />
            <span className="r14 text-[--Dark-Grey]">
              { t('login:remember_me') }
            </span>
          </FlexRow>
          <FlexRow direction="column"
                   flex={0}
                   align="end"
                   className="gap-[6px] p-[8px] text-[16px]">
            <span onClick={() => setForgotPass(true)}
                  className="text-[--Primetime-main] cursor-pointer hover:underline hover:font-bold">
              { t('login:forgot:title') }
            </span>
          </FlexRow>
          <Button caption={t('login:login')}
                  width="w-full"
                  layout={cannotProjectLogin ? 'warning_bg' : 'default'}
                  isDisabled={cannotProjectLogin}
                  onClick={handleProjectLogin} />
          <div className="flex justify-center text-[--Dark-Grey] text-[13px] p-[4px]">
            <span onClick={backToLogin}
                  className="flex items-center gap-[4px] cursor-pointer hover:underline">
              <span className="material-icons text-[16px]">arrow_back</span>
              { t('login:back_to_login') }
            </span>
          </div>
        </FlexRow>
        <FlexRow flex={0}
                 direction="column"
                 className="pb-[32px]">
        </FlexRow>
      </FlexRow>
    {/* Local-user reset: scope it to this project. The typed-code confirm
        REQUIRES project_id for a local user, so we hand the modal the project
        the user is signing in to (and prefill the username they typed).
        Rendered here because this branch returns before the main modal below. */}
    {forgotPass &&
      <ForgotPasswordModal onClose={() => setForgotPass(false)}
                           projectId={projectId}
                           username={user} />
    }
    </FlexRow>
    );
  }

  return (
  <FlexRow direction="column"
           justify="center"
           className="absolute right-0 left-0 top-0 bottom-0 bg-[--Light-Grey]">
    <FlexRow direction="column"
             flex={0}
             className="
               max-w-[560px]
               min-h-[360px]
               loginBanner">
      <FlexRow className="bg-[--Primetime-main]"
               justify="center"
               flex={0}>
        <img src="/assets/img/logo_lg.png"
             className="w-[164px]" />
      </FlexRow>
      <FlexRow direction="column"
               justify="center"
               className="px-[32px] py-[16px] gap-[16px] bg-white">
        <FlexRow direction="column"
                 justify="center">
          <h1 className="r14 text-black font-bold">
            { t('login:caption') }
          </h1>
        </FlexRow>

        { !requires2FA &&
          <>
            <Input value={user}
                   label={t('login:username:caption') + '*'}
                   onValueChange={setUser}
                   type="text"
                   labelPlacement="outside"
                   placeholder={t('login:username:caption')}
                   isInvalid={triedSave && userInvalid}
                   errorMessage={t('login:username:invalid')}
                   classNames={{
                     inputWrapper: "px-[16px] py-[8px] rounded-[4px] border-solid border-1 border-[--Mid-Grey] !bg-white",
                     input: "!text-black",
                     label: "!text-[--Dark-Grey] pb-[4px]",
                   }}
                   endContent={
                   <span className="material-icons cursor-pointer"
                         onClick={() => setUser("")}>
                     refresh
                   </span>
                   } />
            <Input value={password}
                   label={t('login:password:caption') + '*'}
                   onValueChange={setPassword}
                   type={showPw ? 'text' : 'password'}
                   labelPlacement="outside"
                   placeholder={t('login:password:caption')}
                   isInvalid={triedSave && passwordInvalid}
                   errorMessage={t('login:password:invalid')}
                   classNames={{
                     inputWrapper: "px-[16px] py-[8px] rounded-[4px] border-solid border-1 border-[--Mid-Grey] !bg-white",
                     input: "!text-black",
                     label: "!text-[--Dark-Grey] pb-[4px]",
                   }}
                   endContent={
                   <span className="material-icons  cursor-pointer"
                         onClick={() => setShowPw(!showPw)}>
                   { showPw ? 'visibility_off' : 'visibility' }
                   </span>
                   } />

            <FlexRow flex={0}
                     className="gap-[8px] items-center cursor-pointer select-none"
                     onClick={() => setRememberMe(!rememberMe)}>
              <CheckBox toggled={rememberMe}
                        setToggled={setRememberMe}
                        size="text-[24px]" />
              <span className="r14 text-[--Dark-Grey]">
                { t('login:remember_me') }
              </span>
            </FlexRow>
            <FlexRow direction="column"
                     flex={0}
                     align="end"
                     className="gap-[6px] p-[8px] text-[16px]">
              <span onClick={() => setForgotPass(true)}
                    className="text-[--Primetime-main] cursor-pointer hover:underline hover:font-bold">
                { t('login:forgot:title') }
              </span>
              <span onClick={() => setProjectLogin(true)}
                    className="text-[--Primetime-main] cursor-pointer hover:underline hover:font-bold">
                { t('login:login_to_project') }
              </span>
            </FlexRow>
            <Button caption={t('login:login')}
                    width="w-full"
                    layout={cannotLogin ? 'warning_bg' : 'default'}
                    isDisabled={cannotLogin}
                    onClick={handleLogin} />
          </>
        }

      </FlexRow>
      <FlexRow flex={0}
               direction="column"
               className="pb-[32px]">
      </FlexRow>
    </FlexRow>
    {forgotPass &&
      <ForgotPasswordModal onClose={() => setForgotPass(false)}
                           username={user} />
    }
  </FlexRow>
  );
};

export default Login;
